/* Jam stage: chord notes fall down their lane to the strike line, the microphone (or a tap) plays them,
   and the HUD shows the score, the multiplier, the streak and the accuracy; timed lyrics run below.
   React renders the structure once per game. Everything that moves is updated imperatively, from
   requestAnimationFrame (positions, count-in, lyrics) and from the scorer's events (marks, HUD). */
import { type RefObject, useEffect, useRef, useState } from 'react';
import type { PreparedSong } from '../../content/songs';
import { type AudioEvent, audio } from '../../core/audio';
import { exposeDebug } from '../../core/debug';
import type { ChordRef } from '../../core/dsp';
import {
  BASE_POINTS,
  chordHearing,
  compatible,
  type HeardChord,
  HIT_SCORE,
  type JamChart,
  type JamEvent,
  type JamResult,
  JamScorer,
  jamVocab,
  LATE,
  MAX_MULT,
  MULT_STEP,
} from '../../core/jam';
import { onMic } from '../../core/mic';
import { award } from '../../core/store';
import { chordSymbol, parseChord, pretty } from '../../core/theory';
import { clamp } from '../../core/util';
import { Btn } from '../../ui/Button';
import { ChordDiagram } from '../../ui/ChordDiagram';
import { vars } from '../../ui/style';
import { MicLevelMeter } from '../../ui/widgets';
import { arrange } from '../songs/arrange';
import { LANE_COLORS } from './colors';

/** What plays through the speakers: the count-in only, drums, or the full band (headphones only). */
export type Backing = 'count' | 'drums' | 'full';
export type JamInput = 'mic' | 'screen';
export interface JamOptions {
  backing: Backing;
  /** multiplier of the song's tempo */
  tempo: number;
  input: JamInput;
}

/** Distance between the bottom of the highway and the strike line (px). */
const STRIKE_OFF = 22;

const fmt = (n: number) => Math.round(n).toLocaleString('tr-TR');

/** Count-in clicks, then the chosen backing; nothing here pauses the microphone. */
function gameEvents(song: PreparedSong, chart: JamChart, o: JamOptions): AudioEvent[] {
  const ev: AudioEvent[] = [];
  for (let b = 0; b < chart.countBeats; b++)
    ev.push({ kind: 'drum', drum: b % chart.beats === 0 ? 'clickHi' : 'click', t: b * chart.beat, vel: 0.8 });
  if (o.backing !== 'count') {
    const full = o.backing === 'full';
    const arr = arrange(song, {
      tempo: o.tempo,
      drums: true,
      bass: full,
      chords: full,
      melody: false,
      noGate: true,
      chordVel: 0.8,
    });
    for (const e of arr.ev) if (e.kind) ev.push({ ...e, t: e.t + chart.countIn });
  }
  return ev;
}

interface StageApi {
  tap(lane: number): void;
  quit(): void;
}

export function JamRun({
  song,
  chart,
  opts,
  onDone,
}: {
  song: PreparedSong;
  chart: JamChart;
  opts: JamOptions;
  onDone: (r: JamResult) => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const api = useRef<StageApi | null>(null);
  const side = useRef<((bar: number) => void) | null>(null);
  const done = useRef(onDone);
  done.current = onDone;
  const screen = opts.input === 'screen';

  useEffect(() => {
    const root = stageRef.current;
    if (!root) return;
    const one = <T extends HTMLElement>(sel: string) => root.querySelector(sel) as T;
    const all = <T extends HTMLElement>(sel: string, from: ParentNode = root) =>
      Array.from(from.querySelectorAll<T>(sel));
    const plane = one('.jam-plane');
    const track = one('.jam-track');
    const gems = all('.jam-gem');
    const frets = all('.jam-fret');
    const lines = all('.jl-line');
    const words = lines.map((l) => all('.jl-w', l));
    const lyricsBox = one('.jam-lyrics');
    const count = one('.jam-count');
    const banner = one('.jam-banner');
    const pops = one('.jam-pops');
    const strike = one('.jam-strike');
    const live = one('.jam-live');
    const hud = {
      score: one('.jh-score'),
      mult: one('.jam-mult'),
      multV: one('.jm-v'),
      pips: all('.jm-pip'),
      streak: one('.jh-streak'),
      acc: one('.jh-acc'),
      heard: one('.jh-heard'),
      sec: one('.jh-sec'),
      prog: one('.jh-prog i'),
    };

    const scorer = new JamScorer(chart, Math.round(BASE_POINTS * opts.tempo));
    const vocab = jamVocab(chart);
    const hear = chordHearing(vocab);
    const laneRefs = chart.lanes.map((c) => parseChord(c) as ChordRef);
    const lookahead = clamp(chart.barLen * 1.5, 2.6, 4.6);
    let pps = 100;
    const measure = () => {
      pps = Math.max(40, (plane.clientHeight - STRIKE_OFF) / lookahead);
      track.style.setProperty('--pps', pps.toFixed(3));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(plane);

    const player = audio.play(gameEvents(song, chart, opts), {
      delay: 0.6,
      length: chart.countIn + chart.length + LATE + 0.3,
      noGate: true,
    });
    /** AudioContext time of the first downbeat; mic frames carry the same clock. */
    const startAt = player.t0 + chart.countIn;
    const songTime = () => audio.now() - startAt;

    let finished = false;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (ms: number, fn: () => void) => {
      const id = setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };
    const finish = (aborted: boolean) => {
      if (finished) return;
      finished = true;
      done.current(scorer.result(aborted));
    };

    // ------------------------------------------------------------ feedback
    const pop = (text: string, cls: string, lane?: number) => {
      const el = document.createElement('div');
      el.className = `jam-pop ${cls}`;
      el.textContent = text;
      if (lane != null) {
        el.style.setProperty('--lane', String(lane));
        el.style.setProperty('--lc', LANE_COLORS[lane % LANE_COLORS.length] as string);
      }
      pops.append(el);
      // a wide message on an edge lane ("Yanlış: Fmaj7" on a phone) stays inside the stage
      if (lane != null) {
        const half = el.offsetWidth / 2 + 6;
        const x = el.offsetLeft;
        if (x < half || x > pops.clientWidth - half)
          el.style.left = `${Math.min(Math.max(x, half), pops.clientWidth - half)}px`;
      }
      later(1100, () => el.remove());
    };
    const flashing: Record<string, ReturnType<typeof setTimeout>> = {};
    /** Plays a one-off stage animation (CSS class) and removes the class when it is over. */
    const flash = (cls: string, ms: number) => {
      root.classList.remove(cls);
      void root.offsetWidth; // restart the animation
      root.classList.add(cls);
      clearTimeout(flashing[cls]);
      flashing[cls] = setTimeout(() => root.classList.remove(cls), ms);
    };
    let scoreShown = 0;
    const showHud = () => {
      const s = scorer;
      root.dataset.score = String(s.score);
      root.dataset.mult = String(s.mult);
      root.dataset.streak = String(s.streak);
      hud.multV.textContent = `×${s.mult}`;
      hud.mult.dataset.mult = String(s.mult);
      const lit = s.mult >= MAX_MULT ? MULT_STEP : s.streak % MULT_STEP;
      hud.pips.forEach((p, k) => {
        p.classList.toggle('on', k < lit);
      });
      hud.streak.textContent = String(s.streak);
      hud.acc.textContent = s.judged ? `%${Math.round((100 * s.hits) / s.judged)}` : '–';
      root.style.setProperty('--energy', String(Math.log2(s.mult)));
    };
    const handle = (events: JamEvent[]) => {
      for (const e of events) {
        if (e.type === 'end') {
          finish(false);
          continue;
        }
        if (e.type === 'mult') {
          if (e.mult > e.prev) {
            pop(`×${e.mult}`, 'big');
            flash('mult-up', 600);
            live.textContent = `Çarpan ×${e.mult}`;
            if (e.mult >= MAX_MULT && !screen) award('jam_x8');
          } else if (e.prev > 1) live.textContent = 'Seri bitti, çarpan ×1';
          continue;
        }
        const note = chart.notes[e.bar];
        const gem = gems[e.bar];
        if (!note || !gem) continue;
        gem.dataset.state = e.type;
        if (e.type === 'hit') {
          pop(`+${fmt(e.points)}`, 'good', note.lane);
          pop('', 'burst', note.lane);
        } else if (e.type === 'wrong') {
          const heard = e.heard.root >= 0 ? pretty(chordSymbol(e.heard.root, e.heard.q)) : '';
          pop(heard ? `Yanlış: ${heard}` : 'Yanlış', 'bad', note.lane);
          flash('shake', 500);
        } else {
          pop('Kaçtı', 'bad', note.lane);
          flash('shake', 500);
        }
      }
      if (events.length) showHud();
    };

    // ------------------------------------------------------------ input
    let heardKey = '-';
    const showHeard = (top: HeardChord | null) => {
      const key = top ? `${top.root}|${top.q}` : '';
      if (key === heardKey) return;
      heardKey = key;
      hud.heard.textContent = top ? pretty(chordSymbol(top.root, top.q)) : '–';
      frets.forEach((f, l) => {
        f.classList.toggle('heard', !!top && compatible(top, laneRefs[l] as ChordRef));
      });
    };
    const offMic = screen
      ? null
      : onMic('chord', (c) => {
          if (finished) return;
          const top = c.active ? hear(c.chroma, c.bass) : null;
          handle(scorer.push({ t: c.t - startAt, active: c.active, top }));
          showHeard(top && top.score >= HIT_SCORE ? top : null);
        });
    const tap = (lane: number) => {
      const ref = laneRefs[lane];
      const fret = frets[lane];
      if (finished || !ref || !fret) return;
      fret.classList.add('press');
      later(140, () => fret.classList.remove('press'));
      handle(scorer.tap(songTime(), ref));
    };
    const onKey = (e: KeyboardEvent) => {
      if (!screen || e.metaKey || e.ctrlKey || e.altKey) return;
      const n = Number.parseInt(e.key, 10);
      if (n >= 1 && n <= chart.lanes.length) {
        e.preventDefault();
        tap(n - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    api.current = { tap, quit: () => finish(true) };

    // ------------------------------------------------------------ frame loop
    let lastBar = -2;
    let lastSec = -1;
    let lastCount = '';
    let lastBeat = Number.NEGATIVE_INFINITY;
    let curLine = -2;
    let curWord = -2;
    let lineDone = false;
    const lead = Math.min(1.2, chart.barLen * 0.5);
    const setLine = (k: number, state: string) => {
      const el = lines[k];
      if (el) el.dataset.state = state;
    };
    const renderLyrics = (t: number) => {
      const L = chart.lyrics;
      if (!L.length) return;
      let li = -1;
      for (let k = 0; k < L.length && (L[k]?.t ?? 0) - lead <= t; k++) li = k;
      // a line being sung stays the current one until its last word ends; the next one waits under it
      if (li > 0 && t < (L[li - 1]?.end ?? 0)) li--;
      if (li !== curLine) {
        for (let k = 0; k < lines.length; k++) setLine(k, k === li ? 'cur' : k === li + 1 ? 'next' : '');
        curLine = li;
        curWord = -2;
        lineDone = false;
        lyricsBox.dataset.line = String(li);
      }
      const line = L[li];
      const ws = words[li];
      if (!line || !ws) return;
      if (!lineDone && t > line.end + 0.6) {
        lineDone = true;
        setLine(li, 'done');
      }
      let wi = -1;
      line.words.forEach((w, k) => {
        if (w.t <= t) wi = k;
      });
      // past the end of the last word the whole line is sung (a rest between words is not the end)
      if (wi === line.words.length - 1 && t >= (line.words[wi]?.end ?? 0)) wi = line.words.length;
      if (wi !== curWord) {
        ws.forEach((el, k) => {
          el.dataset.state = k < wi ? 'sung' : k === wi ? 'on' : '';
        });
        curWord = wi;
      }
      const w = line.words[wi];
      const el = ws[wi];
      if (w && el) el.style.setProperty('--p', clamp((t - w.t) / (w.end - w.t), 0, 1).toFixed(3));
    };
    const render = (t: number) => {
      track.style.transform = `translate3d(0, ${(t * pps).toFixed(2)}px, 0)`;
      const bar = scorer.barAt(t);
      if (bar !== lastBar) {
        gems[lastBar]?.classList.remove('now');
        const note = chart.notes[bar];
        gems[bar]?.classList.add('now');
        root.dataset.bar = String(bar);
        root.dataset.chord = note?.chord ?? '';
        frets.forEach((f, l) => {
          f.classList.toggle('want', !!note && note.lane === l);
        });
        side.current?.(bar);
        lastBar = bar;
      }
      let sec = -1;
      for (let k = 0; k < chart.sections.length && (chart.sections[k]?.t ?? 0) - 0.05 <= t; k++) sec = k;
      if (sec !== lastSec) {
        const name = chart.sections[sec]?.name ?? '';
        hud.sec.textContent = name;
        if (sec >= 0) {
          banner.textContent = name;
          banner.classList.remove('show');
          void banner.offsetWidth;
          banner.classList.add('show');
        }
        lastSec = sec;
      }
      const c =
        t < -chart.countIn ? 'Hazır' : t < 0 ? String(Math.ceil(-t / chart.beat)) : t < chart.beat ? 'Çal!' : '';
      if (c !== lastCount) {
        count.textContent = c;
        count.hidden = !c;
        lastCount = c;
      }
      const beat = Math.floor(t / chart.beat + 1e-6);
      if (beat !== lastBeat && t >= -chart.countIn) {
        strike.dataset.beat = String(beat & 1);
        strike.dataset.down = beat % chart.beats === 0 ? '1' : '0';
        lastBeat = beat;
      }
      hud.prog.style.width = `${(clamp(t / chart.length, 0, 1) * 100).toFixed(2)}%`;
      if (scoreShown !== scorer.score) {
        const d = scorer.score - scoreShown;
        scoreShown = Math.abs(d) < 2 ? scorer.score : scoreShown + d * 0.22;
        hud.score.textContent = fmt(scoreShown);
      }
      renderLyrics(t);
    };
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const t = songTime();
      handle(scorer.advance(t));
      if (!finished) render(t);
    };
    showHud();
    render(songTime());
    raf = requestAnimationFrame(loop);
    exposeDebug('jam', {
      songTime,
      bars: chart.notes.map((n) => ({ t: n.t, end: n.end, chord: n.chord })),
    });
    // bring the whole stage into view, below the phone layout's sticky top bar
    const topbar = document.querySelector<HTMLElement>('.topbar');
    const above = topbar && getComputedStyle(topbar).display !== 'none' ? topbar.offsetHeight : 0;
    window.scrollTo({
      top: Math.max(0, root.getBoundingClientRect().top + window.scrollY - above - 10),
      behavior: 'smooth',
    });

    return () => {
      finished = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      player.stop();
      offMic?.();
      window.removeEventListener('keydown', onKey);
      for (const id of timers) clearTimeout(id);
      for (const id of Object.values(flashing)) clearTimeout(id);
      api.current = null;
      exposeDebug('jam', undefined);
    };
  }, [song, chart, opts, screen]);

  const lanes = chart.lanes.length;
  const countBars = Math.round(chart.countIn / chart.barLen);
  return (
    <div
      className={`jam-stage${screen ? ' is-screen' : ''}`}
      ref={stageRef}
      data-bar="-1"
      data-score="0"
      data-mult="1"
      data-streak="0"
      style={vars({ '--lanes': String(lanes) })}
    >
      <div className="jam-hud">
        <div className="jh-box">
          <span className="jh-k">Puan</span>
          <span className="jh-score jh-v">0</span>
          <span className="jh-sec" />
        </div>
        <div className="jam-mult" data-mult="1" title="Çarpan">
          <span className="jm-v">×1</span>
          <span className="jm-pips">
            {Array.from({ length: MULT_STEP }, (_, k) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length list
              <i key={k} className="jm-pip" />
            ))}
          </span>
        </div>
        <div className="jh-box jh-right">
          <span className="jh-k">
            Seri <b className="jh-streak">0</b>
          </span>
          <span className="jh-k">
            İsabet <b className="jh-acc">–</b>
          </span>
        </div>
        <div className="jh-prog" aria-hidden="true">
          <i />
        </div>
      </div>
      <div className="jam-body">
        <div className="jam-road">
          <div className="jam-plane">
            <div className="jam-lanes" aria-hidden="true">
              {chart.lanes.map((c, l) => (
                <span key={c} style={vars({ '--lc': LANE_COLORS[l % LANE_COLORS.length] as string })} />
              ))}
            </div>
            <div
              className="jam-track"
              style={vars({ '--beats': String(chart.beats), '--bar': chart.barLen.toFixed(4) })}
              aria-hidden="true"
            >
              {Array.from({ length: chart.notes.length + countBars }, (_, k) => (
                <span
                  // biome-ignore lint/suspicious/noArrayIndexKey: one line per bar
                  key={k}
                  className="jam-barline"
                  style={vars({ '--t': ((k - countBars) * chart.barLen).toFixed(4) })}
                />
              ))}
              {chart.notes.map((n) => (
                <div
                  key={n.bar}
                  className="jam-gem"
                  data-bar={n.bar}
                  data-chord={n.chord}
                  style={vars({
                    '--t': n.t.toFixed(4),
                    '--d': (n.end - n.t).toFixed(4),
                    '--lane': String(n.lane),
                    '--lc': LANE_COLORS[n.lane % LANE_COLORS.length] as string,
                  })}
                >
                  <span className="jg-tail" />
                  <span className="jg-head">{pretty(n.chord)}</span>
                </div>
              ))}
            </div>
            <div className="jam-strike" />
          </div>
          <div className="jam-frets">
            {chart.lanes.map((c, l) =>
              screen ? (
                <button
                  key={c}
                  type="button"
                  className="jam-fret"
                  aria-label={`${pretty(c)} akorunu çal (${l + 1})`}
                  style={vars({ '--lc': LANE_COLORS[l % LANE_COLORS.length] as string })}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    api.current?.tap(l);
                  }}
                  onClick={(e) => {
                    // screen readers click without a pointer (detail 0); pointers already played on press
                    if (e.detail === 0) api.current?.tap(l);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      api.current?.tap(l);
                    }
                  }}
                >
                  {pretty(c)}
                </button>
              ) : (
                <span
                  key={c}
                  className="jam-fret"
                  style={vars({ '--lc': LANE_COLORS[l % LANE_COLORS.length] as string })}
                >
                  {pretty(c)}
                </span>
              ),
            )}
          </div>
          <div className="jam-pops" aria-hidden="true" />
          <div className="jam-banner" aria-hidden="true" />
          <div className="jam-count" aria-hidden="true" />
        </div>
        <JamSide chart={chart} api={side} />
      </div>
      <div className="jam-lyrics" data-empty={chart.lyrics.length ? 'false' : 'true'} data-line="-1">
        {chart.lyrics.map((line, k) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: lines are positional
          <p key={k} className="jl-line" data-state={k === 0 ? 'next' : ''}>
            {line.words.map((w, j) => (
              <span
                // biome-ignore lint/suspicious/noArrayIndexKey: words are positional
                key={j}
                className="jl-w"
                data-t0={w.t.toFixed(3)}
                data-t1={w.end.toFixed(3)}
              >
                {w.text}
              </span>
            ))}
          </p>
        ))}
      </div>
      <div className="jam-foot">
        <Btn kind="ghost" icon="stop" onClick={() => api.current?.quit()}>
          Bitir
        </Btn>
        {screen ? (
          <span className="jam-tip">
            Akor çizgiye gelince rengine dokun<span className="jt-keys"> (ya da 1–{lanes} tuşları)</span>.
          </span>
        ) : (
          <span className="jam-heard">
            Duyulan: <b className="jh-heard">–</b>
            <MicLevelMeter />
          </span>
        )}
      </div>
      <div className="sr-only jam-live" aria-live="polite" />
    </div>
  );
}

/** Current chord's diagram and the next chord, beside the highway on wide screens. */
function JamSide({ chart, api }: { chart: JamChart; api: RefObject<((bar: number) => void) | null> }) {
  const [bar, setBar] = useState(-1);
  useEffect(() => {
    api.current = setBar;
    return () => {
      api.current = null;
    };
  }, [api]);
  const now = chart.notes[Math.max(0, bar)];
  const next = chart.notes[Math.max(0, bar) + 1];
  if (!now) return null;
  return (
    <aside className="jam-side" aria-label="Akor şeması">
      <span className="js-k">{bar < 0 ? 'İlk akor' : 'Şimdi'}</span>
      <ChordDiagram chord={now.chord} onClick={false} />
      <span className="js-k">Sıradaki</span>
      <span className="js-next">{next ? pretty(next.chord) : 'Son'}</span>
    </aside>
  );
}
